import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { getStorage } from '../lib/utils/safeStorage';
import {
  DEFAULT_PARAMETER_WORKFLOW_MODE,
  DEFAULT_PARAMETER_WORKFLOW_PARAMETERS as defaultParameters,
  DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES as defaultSelectedCategories,
  DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
  DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
} from '../config/parameterWorkflowDefaults';
import type { SharedParameterDataType } from '../types/sharedParameter.types';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Parameter selection option
 */
export interface ParameterOption {
  /** Parameter name */
  name: string;
  /** Whether parameter is selected */
  selected: boolean;
  /** Parameter description */
  description?: string;
  /** Shared-parameter data type (TEXT, LENGTH, YESNO, ...) */
  dataType?: SharedParameterDataType;
}

/**
 * Category option for Revit parameters
 */
export type RevitCategory =
  | 'PIPES'
  | 'PIPE FITTINGS'
  | 'PIPE ACCESSORIES'
  | 'GENERIC MODELS'
  | 'MECHANICAL EQUIPMENT';

/**
 * Represents a family that has parameters added to it during workflow
 */
export interface WorkflowFamily {
  id: string;
  name: string;
  category: string;
  addedParameterNames: string[]; // Parameter names that were added to this family
}

const EMPTY_PARAMETER_OPTIONS: ParameterOption[] = [];
const EMPTY_REVIT_CATEGORIES: RevitCategory[] = [];
const EMPTY_WORKFLOW_FAMILIES: WorkflowFamily[] = [];

/**
 * Workflow mode - legacy (old checkbox selection) or multipage (new workflow)
 */
export type WorkflowMode = 'legacy' | 'multipage';

// ============================================================================
// State Interface
// ============================================================================

interface ParameterWorkflowState {
  // Workflow mode
  workflowMode: WorkflowMode;

  // Shared parameter file selection
  sharedParameterFilePath: string;
  useDefaultFile: boolean;

  // Parameter selection
  parameters: ParameterOption[];

  // Category selection
  selectedCategories: RevitCategory[];

  // Families with added parameters (for multipage workflow)
  familiesWithParameters: WorkflowFamily[];

  // System grouping preference (moved from menu for clarity)
  systemGroupingPreference: string;

  // Validation
  isValid: boolean;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface ParameterWorkflowActions {
  // Workflow mode
  setWorkflowMode: (mode: WorkflowMode) => void;

  // File path actions
  setSharedParameterFilePath: (path: string) => void;
  setUseDefaultFile: (useDefault: boolean) => void;

  // Parameter selection actions
  toggleParameter: (parameterName: string) => void;
  setParameterSelected: (parameterName: string, selected: boolean) => void;
  setAllParameters: (parameters: ParameterOption[]) => void;

  // Category selection actions
  toggleCategory: (category: RevitCategory) => void;
  setCategorySelected: (category: RevitCategory, selected: boolean) => void;
  setSelectedCategories: (categories: RevitCategory[]) => void;

  // Family management (for multipage workflow)
  addFamilyWithParameters: (family: WorkflowFamily) => void;
  removeFamilyWithParameters: (familyId: string) => void;
  updateFamilyParameters: (familyId: string, parameterNames: string[]) => void;
  clearFamiliesWithParameters: () => void;

  // System grouping
  setSystemGroupingPreference: (preference: string) => void;

  // Validation
  validateConfiguration: () => boolean;

  // Reset
  resetConfiguration: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ParameterWorkflowState = {
  workflowMode: DEFAULT_PARAMETER_WORKFLOW_MODE,
  sharedParameterFilePath: DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
  useDefaultFile: true,
  parameters: defaultParameters,
  selectedCategories: defaultSelectedCategories,
  familiesWithParameters: [],
  systemGroupingPreference: DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
  isValid: false,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useParameterWorkflowStore = create<ParameterWorkflowState & ParameterWorkflowActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Workflow mode
      setWorkflowMode: (mode) => {
        set({ workflowMode: mode });
      },

      // File path actions
      setSharedParameterFilePath: (path) => {
        set({ sharedParameterFilePath: path });
        get().validateConfiguration();
      },

      setUseDefaultFile: (useDefault) => {
        set({ useDefaultFile: useDefault });
        if (useDefault) {
          set({ sharedParameterFilePath: DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH });
        }
        get().validateConfiguration();
      },

      // Parameter selection actions
      toggleParameter: (parameterName) => {
        set((state) => {
          const params = Array.isArray(state.parameters) ? state.parameters : [];
          return {
            parameters: params.map((param) =>
              param && param.name === parameterName
                ? { ...param, selected: !param.selected }
                : param
            ),
          };
        });
        get().validateConfiguration();
      },

      setParameterSelected: (parameterName, selected) => {
        set((state) => {
          const params = Array.isArray(state.parameters) ? state.parameters : [];
          return {
            parameters: params.map((param) =>
              param && param.name === parameterName
                ? { ...param, selected }
                : param
            ),
          };
        });
        get().validateConfiguration();
      },

      setAllParameters: (parameters) => {
        set({ parameters });
        get().validateConfiguration();
      },

      // Category selection actions
      toggleCategory: (category) => {
        set((state) => {
          const isSelected = state.selectedCategories.includes(category);
          return {
            selectedCategories: isSelected
              ? state.selectedCategories.filter((c) => c !== category)
              : [...state.selectedCategories, category],
          };
        });
        get().validateConfiguration();
      },

      setCategorySelected: (category, selected) => {
        set((state) => {
          const isCurrentlySelected = state.selectedCategories.includes(category);
          if (selected && !isCurrentlySelected) {
            return { selectedCategories: [...state.selectedCategories, category] };
          } else if (!selected && isCurrentlySelected) {
            return { selectedCategories: state.selectedCategories.filter((c) => c !== category) };
          }
          return state;
        });
        get().validateConfiguration();
      },

      setSelectedCategories: (categories) => {
        set({ selectedCategories: categories });
        get().validateConfiguration();
      },

      // Family management (for multipage workflow)
      addFamilyWithParameters: (family) => {
        set((state) => ({
          familiesWithParameters: [...state.familiesWithParameters, family],
        }));
      },

      removeFamilyWithParameters: (familyId) => {
        set((state) => ({
          familiesWithParameters: state.familiesWithParameters.filter((f) => f.id !== familyId),
        }));
      },

      updateFamilyParameters: (familyId, parameterNames) => {
        set((state) => ({
          familiesWithParameters: state.familiesWithParameters.map((f) =>
            f.id === familyId ? { ...f, addedParameterNames: parameterNames } : f
          ),
        }));
      },

      clearFamiliesWithParameters: () => {
        set({ familiesWithParameters: [] });
      },

      // System grouping
      setSystemGroupingPreference: (preference) => {
        set({ systemGroupingPreference: preference });
        get().validateConfiguration();
      },

      // Validation
      validateConfiguration: () => {
        const state = get();

        // Validation rules with safety checks:
        // 1. Must have a shared parameter file path (either default or custom)
        const hasValidFilePath = state.useDefaultFile || !!(state.sharedParameterFilePath && state.sharedParameterFilePath.trim() !== '');

        // 2. At least one parameter must be selected (with array safety)
        const params = Array.isArray(state.parameters) ? state.parameters : [];
        const hasSelectedParameters = params.some((param) => param && param.selected);

        // 3. At least one category must be selected (with array safety)
        const categories = Array.isArray(state.selectedCategories) ? state.selectedCategories : [];
        const hasSelectedCategories = categories.length > 0;

        // 4. System grouping preference must be set
        const hasSystemGrouping = !!(state.systemGroupingPreference && state.systemGroupingPreference.trim() !== '');

        const isValid = hasValidFilePath && hasSelectedParameters && hasSelectedCategories && hasSystemGrouping;

        set({ isValid });
        return isValid;
      },

      // Reset
      resetConfiguration: () => {
        set(initialState);
      },
    }),
    {
      name: 'pipeex-parameter-workflow-store',
      version: 2,
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Persist all configuration
        workflowMode: state.workflowMode,
        sharedParameterFilePath: state.sharedParameterFilePath,
        useDefaultFile: state.useDefaultFile,
        parameters: state.parameters,
        selectedCategories: state.selectedCategories,
        familiesWithParameters: state.familiesWithParameters,
        systemGroupingPreference: state.systemGroupingPreference,
      }),
      // Migration to handle corrupted or old data
      migrate: (persistedState: any, version: number) => {
        void version;

        // If no persisted state, return defaults
        if (!persistedState) {
          return {
            workflowMode: DEFAULT_PARAMETER_WORKFLOW_MODE,
            sharedParameterFilePath: DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
            useDefaultFile: true,
            parameters: defaultParameters,
            selectedCategories: defaultSelectedCategories,
            familiesWithParameters: [],
            systemGroupingPreference: DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
          };
        }

        const persistedCategories = Array.isArray(persistedState.selectedCategories)
          ? persistedState.selectedCategories
          : [];
        const migratedCategories = version < 2
          ? Array.from(new Set([...persistedCategories, 'GENERIC MODELS', 'MECHANICAL EQUIPMENT']))
          : persistedCategories;

        // Ensure arrays are actually arrays
        return {
          workflowMode: persistedState.workflowMode || DEFAULT_PARAMETER_WORKFLOW_MODE,
          sharedParameterFilePath:
            persistedState.sharedParameterFilePath ||
            DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
          useDefaultFile: persistedState.useDefaultFile ?? true,
          parameters: Array.isArray(persistedState.parameters)
            ? persistedState.parameters
            : defaultParameters,
          selectedCategories:
            migratedCategories.length > 0 ? migratedCategories : defaultSelectedCategories,
          familiesWithParameters: Array.isArray(persistedState.familiesWithParameters)
            ? persistedState.familiesWithParameters
            : [],
          systemGroupingPreference:
            persistedState.systemGroupingPreference ||
            DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
        };
      },
      onRehydrateStorage: () => () => {},
    }
  )
);

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

export const useSharedParameterFilePath = () =>
  useParameterWorkflowStore((state) => state.sharedParameterFilePath);

export const useUseDefaultFile = () =>
  useParameterWorkflowStore((state) => state.useDefaultFile);

export const useParameters = () =>
  useParameterWorkflowStore((state) =>
    Array.isArray(state.parameters) ? state.parameters : EMPTY_PARAMETER_OPTIONS
  );

export const useSelectedParameters = () =>
  useParameterWorkflowStore(
    useShallow((state) => {
      const params = Array.isArray(state.parameters) ? state.parameters : [];
      return params.filter((p) => p && p.selected);
    })
  );

export const useSelectedCategories = () =>
  useParameterWorkflowStore((state) =>
    Array.isArray(state.selectedCategories) ? state.selectedCategories : EMPTY_REVIT_CATEGORIES
  );

export const useSystemGroupingPreference = () =>
  useParameterWorkflowStore((state) => state.systemGroupingPreference);

export const useParameterWorkflowIsValid = () =>
  useParameterWorkflowStore((state) => state.isValid);

export const useWorkflowMode = () =>
  useParameterWorkflowStore((state) => state.workflowMode);

export const useFamiliesWithParameters = () =>
  useParameterWorkflowStore((state) =>
    Array.isArray(state.familiesWithParameters)
      ? state.familiesWithParameters
      : EMPTY_WORKFLOW_FAMILIES
  );
